// https://www.figma.com/design/8b6QnKR8kkpkjjDLYRdMNO/-External-_Parkd_Brand_Web?node-id=381-6428&t=0HeOxyVzYSfbnBcV-0

// https://bitbucket.org/pickmeupantwerp/parkd-design/src/master/company-registration/track_trace-sequence-flow-v4.puml

// BE0785477294 (Parkd NV)

import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { formatMessage } from 'i18n/CurrentLocale';
import { FormattedMessage } from 'react-intl';
import RegAlreadyLoggedIn from 'screens/registration/RegAlreadyLoggedIn';
import { connect } from 'react-redux';
import { CompanyDataFromVAT, ReduxState } from 'types';
import { getRegistrationFlowTypeFromURL, initialFormData_OEM, initialFormData_TRACK_AND_TRACE, initialTouchedFields, mapUserLanguageToCountry, RegistrationField, TouchedFields } from './utils';
import { FormRegistrationData } from './validationSchemas';
import { bindActionCreators } from '@reduxjs/toolkit';
import { registrationV2Slice } from 'features/registration-v2/registrationV2Slice';
import { createTestRegistrationFormData } from '__test_resources__/factoryMethods';
import { TelematicPartnerLogo } from './TelematicPartnerLogo';
import { StepCompanyDetails } from './StepCompanyDetails';
import { ProgressBar } from './ProgressBar';
import { CarouselQuotes } from './CarouselQuotes';
import { StepsIndicator } from './StepsIndicator';
import { StepAccountDetails } from './StepAccountDetails';
import StepPaymentOptions from './StepPaymentOptions';
import { Link } from 'react-router';
import { ObjectSchema } from 'yup';
import { sortCountries } from 'myParkdUtils/utils';
import UpperNavBar from 'components/board/UpperNavBar';
import rawstrings from 'string-translations/rawstrings';
import countries from 'world-countries';
import * as Sentry from '@sentry/react';


export const countryCodes = countries.sort(sortCountries).map((country) => country.cca2);

const totalSteps = 3;

export const RegistrationScreen = (props: Props) => {

    const flowType = useMemo(
        () => getRegistrationFlowTypeFromURL(), []
    );

    const initialFormData = flowType === 'OEM'
        ? initialFormData_OEM
        : initialFormData_TRACK_AND_TRACE;

    const [formData, setFormData] = useState<FormRegistrationData>(initialFormData);
    const [errors, setErrors] = useState<FormRegistrationData>();
    const [touched, setTouched] = useState<TouchedFields>(initialTouchedFields);
    const [formStep, setFormStep] = useState<number>(1);

    useEffect(() => {
        props.resetRegistrationFlow();
    }, []);

    useEffect(() => {
        const country = mapUserLanguageToCountry(props.userLanguage);
        if (!touched?.addressCountry) {
            setFormData((prevState) => ({
                ...prevState,
                addressCountry: country
            }));
        }
    }, [props.userLanguage]);


    const getClassNames = (name: RegistrationField) => errors?.[name] ? 'error' : 'valid';

    const handleUserInput = (e: ChangeEvent<HTMLInputElement>) => {
        setFormData((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value
        }))
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setTouched((prevState) => ({
            ...prevState,
            [e.target.name]: true
        }))
    }

    const prefillCompanyFieldsBasedOnVAT = async (companyData: CompanyDataFromVAT) => {
        setFormData((prevState) => ({
            ...prevState,
            ...(companyData?.name && { companyName: companyData.name }),
            ...(companyData?.address?.street && { addressStreet: companyData.address.street }),
            ...(companyData?.address?.number && { addressNumber: companyData.address.number }),
            ...(companyData?.address?.city && { addressCity: companyData.address.city }),
            ...(companyData?.address?.postalCode && { addressPostalCode: companyData.address.postalCode }),
            ...(companyData?.address?.countryCode && { addressCountry: companyData.address.countryCode }),
        }));
        setTouched((prevState) => ({
            ...prevState,
            companyName: !!companyData?.name,
            addressStreet: !!companyData?.address?.street,
            addressNumber: !!companyData?.address?.number,
            addressCity: !!companyData?.address?.city,
            addressPostalCode: !!companyData?.address?.postalCode,
            addressCountry: !!companyData?.address?.countryCode
        }));
    }

    const validateForm = async (schema: ObjectSchema<any>, context: any) => {
        return schema.validate(formData, { abortEarly: false, context: { ...context, flowType } })
            .then((res) => {
                return { isValid: true, errors: {} }
            })
            .catch((err) => {
                const errors = err.inner.reduce((acc, error) => {
                    acc[error.path] = error.message;
                    return acc;
                }, {});

                return { isValid: false, errors };
            })
    }

    if (props.token && formStep === 1) {
        return <RegAlreadyLoggedIn />;
    }

    const telematicPartnerLogo = (
        <TelematicPartnerLogo flowType={flowType} updateTelematicProvider={(telematicProvider) => {
            setFormData((prevState) => ({
                ...prevState,
                telematicProvider
            }))
        }}
        />
    )

    return (
        <div id="page-registration" className="page-full-height">
            <header className="header-row">
                <div className='grid'>
                    <UpperNavBar
                        backgroundColor="white"
                        logoRedirecsTo={formatMessage(rawstrings.parkdWebsiteHomeUrl)}
                        openInNewTab
                        showSupportPhoneNumber
                        telematicPartnerLogo={telematicPartnerLogo}
                    />
                </div>
                <ProgressBar progress={(formStep / totalSteps) * 100} />
            </header>

            <div className="flex-1-content">
                <aside className="column-left">

                    <div className='grid'>
                        <div className='pad-2-0'>
                            <StepsIndicator
                                currentStep={formStep}
                                totalSteps={totalSteps}
                            />
                        </div>

                        {formStep === 1 &&
                            <StepCompanyDetails
                                flowType={flowType}
                                formData={formData}
                                touched={touched}
                                errors={errors}
                                setErrors={setErrors}
                                setTouched={setTouched}
                                setFormData={setFormData}
                                setFormStep={setFormStep}
                                handleBlur={handleBlur}
                                handleUserInput={handleUserInput}
                                getClassNames={getClassNames}
                                prefillCompanyFieldsBasedOnVAT={prefillCompanyFieldsBasedOnVAT}
                                validateForm={validateForm}
                            />
                        }

                        {formStep === 2 &&
                            <StepAccountDetails
                                flowType={flowType}
                                formData={formData}
                                touched={touched}
                                errors={errors}
                                setErrors={setErrors}
                                setTouched={setTouched}
                                setFormData={setFormData}
                                setFormStep={setFormStep}
                                handleBlur={handleBlur}
                                handleUserInput={handleUserInput}
                                getClassNames={getClassNames}
                                userLanguage={props.userLanguage}
                                validateForm={validateForm}
                            />
                        }

                        {formStep === 3 &&
                            <StepPaymentOptions
                                flowType={flowType}
                                formData={formData}
                                setFormStep={setFormStep}
                                registerV2={props.registerV2}
                                userLanguage={props.userLanguage}
                            />
                        }

                        {formStep === 1 && (
                            <div className='col-lower'>
                                <div className='center'>
                                    <FormattedMessage id='ALREADY_HAVE_AN_ACCOUNT' />&nbsp;
                                    <Link to='/login' className='text-link'>
                                        <FormattedMessage id='GLOBAL_CTA_LOGIN' />
                                    </Link>
                                </div>
                            </div>
                        )}

                    </div>
                </aside>

                <main className="column-right">
                    <CarouselQuotes formStep={formStep} />
                </main>

            </div>
        </div >
    )
}


type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps;

function mapDispatchToProps(dispatch: any) {
    return bindActionCreators({
        resetRegistrationFlow: registrationV2Slice.actions.reset,
        registerV2: registrationV2Slice.actions.registerV2
    }, dispatch);
}

function mapStateToProps(state: ReduxState) {
    return {
        token: state.auth?.token,
        userLanguage: state?.userLanguage?.userLanguage,
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(RegistrationScreen);